// Cinematic one-shot overlay when a player is eliminated — the visual is
// chosen by the elimination `method` so each cause-of-death feels distinct.
// The overlay self-dismisses after a few seconds and is keyed on a stable
// "round + method + victim" id so it only plays once per event.

import { useEffect, useState } from 'react';
import type { Lang } from '../../../shared/src/types.js';
import { usePerfLite } from '../usePerfLite.js';

type Method =
  | 'vote'
  | 'mafia'
  | 'maniac'
  | 'arsonist'
  | 'vigilante'
  | 'alert'
  | 'bodyguard'
  | 'werewolf';

interface Props {
  t: Lang;
  victimName: string;
  method: Method;
  // Anything that uniquely identifies the kill (e.g. day-N + victim id).
  // When this changes, the animation replays.
  signature: string;
  durationMs?: number;
}

const HEAD_COPY: Record<Method, Record<Lang, { title: string; sub: string }>> = {
  mafia: {
    ru: { title: 'УБИТ', sub: 'МАФИЯ НАВЕСТИЛА' },
    uk: { title: 'ВБИТО', sub: 'МАФІЯ ЗАЇХАЛА' },
  },
  vote: {
    ru: { title: 'ПОВЕШЕН', sub: 'ПРИГОВОР ТОЛПЫ' },
    uk: { title: 'ПОВІШЕНО', sub: 'ВИРОК НАТОВПУ' },
  },
  arsonist: {
    ru: { title: 'СГОРЕЛ', sub: 'ПОДЖИГАТЕЛЬ ВЫШЕЛ НА ОХОТУ' },
    uk: { title: 'ЗГОРІВ', sub: 'ПАЛІЙ ВИЙШОВ НА ПОЛЮВАННЯ' },
  },
  maniac: {
    ru: { title: 'ЗАРЕЗАН', sub: 'МАНЬЯК НЕ ДРЕМАЛ' },
    uk: { title: 'ЗАРІЗАНО', sub: 'МАНІЯК НЕ СПАВ' },
  },
  vigilante: {
    ru: { title: 'ЗАСТРЕЛЕН', sub: 'ОДИНОЧНЫЙ ВЫСТРЕЛ' },
    uk: { title: 'ЗАСТРЕЛЕНО', sub: 'ОДИНОЧНИЙ ПОСТРІЛ' },
  },
  alert: {
    ru: { title: 'ПОДОРВАЛСЯ', sub: 'ВЕТЕРАН БЫЛ НА ВЗВОДЕ' },
    uk: { title: 'ПІДІРВАНО', sub: 'ВЕТЕРАН БУВ НА ЗВЕДЕННІ' },
  },
  bodyguard: {
    ru: { title: 'ПАЛ В БОЮ', sub: 'ТЕЛОХРАНИТЕЛЬ ПРИНЯЛ ПУЛЮ' },
    uk: { title: 'ПОЛІГ', sub: 'ТІЛОХОРОНЕЦЬ ПРИЙНЯВ КУЛЮ' },
  },
  werewolf: {
    ru: { title: 'РАСТЕРЗАН', sub: 'ЛИКАНТРОП ВЫШЕЛ НА ЛУНУ' },
    uk: { title: 'РОЗТЕРЗАНО', sub: 'ЛІКАНТРОП ВИЙШОВ НА МІСЯЦЬ' },
  },
};

export function DeathScene({ t, victimName, method, signature, durationMs = 3200 }: Props) {
  const lite = usePerfLite();
  const [playing, setPlaying] = useState(true);

  // Visibility is driven directly off `signature` (it changes per elimination,
  // so the effect re-runs exactly when a replay is wanted). There is no
  // `shownSig` early-return, which keeps React 18 StrictMode's double-invoke
  // harmless: each mount arms a fresh timer and its cleanup clears the stale one.
  //
  // Unmount is normally driven by the CSS `ds-exit` animation completing (see
  // onAnimationEnd below). The timer here is a robust fallback that is ALWAYS
  // present — and is the SOLE dismissal path under perf-lite, where every
  // animation is killed (`animation: none !important`) so `ds-exit` never fires.
  // Under lite we hold for a short, fixed beat instead of waiting out the full
  // cinematic, since there is no entry/exit animation to play.
  useEffect(() => {
    setPlaying(true);
    // Full FX: give the CSS exit a small buffer to land first so the unmount is
    // driven by `animationend`, not the timer. Lite: no animation, short hold.
    const hold = lite ? 1200 : durationMs + 200;
    const id = setTimeout(() => setPlaying(false), hold);
    return () => clearTimeout(id);
  }, [signature, durationMs, lite]);

  if (!playing) return null;

  const copy = HEAD_COPY[method]?.[t] ?? HEAD_COPY.mafia[t];

  return (
    <div
      className={`death-scene ds-${method}`}
      onClick={() => setPlaying(false)}
      onAnimationEnd={(e) => {
        // Only the exit fade should dismiss; ds-enter / ds-shake also bubble here.
        if (e.animationName === 'ds-exit') setPlaying(false);
      }}
    >
      <div className="ds-shake">
        <DeathArt method={method} />
      </div>
      <div className="ds-copy">
        <div className="ds-eyebrow mono">{copy.sub}</div>
        <div className="ds-title display">{copy.title}</div>
        <div className="ds-name display">{victimName.toUpperCase()}</div>
      </div>
    </div>
  );
}

function DeathArt({ method }: { method: Method }) {
  if (method === 'mafia') {
    // Knife slash — long red diagonal stroke + splatter dots.
    return (
      <svg viewBox="0 0 400 400" className="ds-art">
        <defs>
          <filter id="ds-blur"><feGaussianBlur stdDeviation="0.6" /></filter>
        </defs>
        <line className="ds-slash" x1="40" y1="340" x2="360" y2="60" stroke="#ff2a3c" strokeWidth="14" strokeLinecap="round" />
        <g filter="url(#ds-blur)">
          <circle className="ds-drop d1" cx="120" cy="120" r="14" fill="#ff2a3c" />
          <circle className="ds-drop d2" cx="270" cy="180" r="22" fill="#ff2a3c" />
          <circle className="ds-drop d3" cx="180" cy="260" r="10" fill="#ff2a3c" />
          <circle className="ds-drop d4" cx="320" cy="300" r="16" fill="#ff2a3c" />
          <circle className="ds-drop d5" cx="80" cy="220" r="9" fill="#ff2a3c" />
        </g>
      </svg>
    );
  }
  if (method === 'arsonist') {
    return (
      <svg viewBox="0 0 400 400" className="ds-art">
        <g className="ds-flame">
          <path d="M200 320 Q120 240 160 160 Q170 200 200 180 Q190 120 240 80 Q230 160 270 160 Q260 240 200 320 Z"
            fill="#ff7a1a" />
          <path d="M200 300 Q150 240 180 180 Q190 220 210 200 Q200 160 230 130 Q220 200 245 200 Q235 250 200 300 Z"
            fill="#ffd166" opacity="0.85" />
        </g>
        <circle className="ds-spark s1" cx="120" cy="200" r="4" fill="#ffd166" />
        <circle className="ds-spark s2" cx="290" cy="170" r="3" fill="#ff7a1a" />
        <circle className="ds-spark s3" cx="170" cy="100" r="3" fill="#ffd166" />
        <circle className="ds-spark s4" cx="260" cy="240" r="4" fill="#ff7a1a" />
      </svg>
    );
  }
  if (method === 'vote') {
    return (
      <svg viewBox="0 0 400 400" className="ds-art">
        <line className="ds-rope" x1="200" y1="0" x2="200" y2="220" stroke="#a89c8b" strokeWidth="6" />
        <ellipse className="ds-noose" cx="200" cy="260" rx="62" ry="46" fill="none" stroke="#a89c8b" strokeWidth="10" />
        <line x1="160" y1="220" x2="240" y2="220" stroke="#a89c8b" strokeWidth="6" />
      </svg>
    );
  }
  if (method === 'maniac') {
    return (
      <svg viewBox="0 0 400 400" className="ds-art">
        <g className="ds-glitch">
          <rect x="60" y="60" width="280" height="280" fill="none" stroke="#ff2a3c" strokeWidth="3" />
          <line x1="60" y1="120" x2="340" y2="120" stroke="#ff2a3c" strokeWidth="2" opacity="0.7" />
          <line x1="60" y1="200" x2="340" y2="200" stroke="#b6ff3a" strokeWidth="2" opacity="0.6" />
          <line x1="60" y1="280" x2="340" y2="280" stroke="#3aa7ff" strokeWidth="2" opacity="0.5" />
        </g>
        <text x="200" y="220" textAnchor="middle"
          fontFamily="Big Shoulders Stencil"
          fontSize="120" fontWeight="900" fill="#ff2a3c"
          className="ds-glitch-text">×</text>
      </svg>
    );
  }
  if (method === 'vigilante') {
    return (
      <svg viewBox="0 0 400 400" className="ds-art">
        <g className="ds-flash">
          <circle cx="200" cy="200" r="110" fill="#ffd166" opacity="0.9" />
          <circle cx="200" cy="200" r="60" fill="#fff" opacity="0.95" />
        </g>
        <g className="ds-rays">
          {Array.from({ length: 12 }).map((_, i) => (
            <line key={i} x1="200" y1="200" x2={200 + Math.cos((i / 12) * Math.PI * 2) * 180}
              y2={200 + Math.sin((i / 12) * Math.PI * 2) * 180}
              stroke="#ffd166" strokeWidth="3" opacity="0.6" />
          ))}
        </g>
        <circle className="ds-hole" cx="200" cy="200" r="22" fill="#0a0a0c" />
      </svg>
    );
  }
  if (method === 'alert') {
    return (
      <svg viewBox="0 0 400 400" className="ds-art">
        <g className="ds-flash">
          <circle cx="200" cy="200" r="120" fill="#ff2a3c" opacity="0.85" />
        </g>
        <g className="ds-shrap">
          {Array.from({ length: 14 }).map((_, i) => {
            const a = (i / 14) * Math.PI * 2;
            return (
              <line
                key={i}
                x1={200 + Math.cos(a) * 60}
                y1={200 + Math.sin(a) * 60}
                x2={200 + Math.cos(a) * 180}
                y2={200 + Math.sin(a) * 180}
                stroke="#ffd166"
                strokeWidth="4"
              />
            );
          })}
        </g>
      </svg>
    );
  }
  if (method === 'bodyguard') {
    return (
      <svg viewBox="0 0 400 400" className="ds-art">
        <g className="ds-shield">
          <path
            d="M200 60 L320 110 L320 240 Q320 320 200 360 Q80 320 80 240 L80 110 Z"
            fill="none"
            stroke="#c8d0d6"
            strokeWidth="10"
          />
          <path d="M200 60 L320 110 L320 240" fill="none" stroke="#c8d0d6" strokeWidth="6" opacity="0.6" />
        </g>
        <line className="ds-crack c1" x1="200" y1="100" x2="120" y2="280" stroke="#0a0a0c" strokeWidth="6" />
        <line className="ds-crack c2" x1="200" y1="100" x2="280" y2="290" stroke="#0a0a0c" strokeWidth="6" />
        <line className="ds-crack c3" x1="200" y1="100" x2="200" y2="320" stroke="#0a0a0c" strokeWidth="6" />
      </svg>
    );
  }
  if (method === 'werewolf') {
    return (
      <svg viewBox="0 0 400 400" className="ds-art">
        <g className="ds-claw">
          <path d="M60 80 L340 320" stroke="#ff2a3c" strokeWidth="14" strokeLinecap="round" />
          <path d="M60 160 L340 360" stroke="#ff2a3c" strokeWidth="14" strokeLinecap="round" />
          <path d="M60 240 L300 360" stroke="#ff2a3c" strokeWidth="14" strokeLinecap="round" />
        </g>
        <circle cx="320" cy="80" r="36" fill="#f0e6d4" opacity="0.85" />
      </svg>
    );
  }
  return null;
}
